import {datadogRum} from '@datadog/browser-rum';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {useEffect, useMemo} from 'react';
import {isRegionInUnitedStates} from 'utils/aws-utils';
import {formatVersionForDatadog} from 'utils/datadog-utils';
import Logger from 'utils/logger';
import * as EnvironmentConstants from '../constants/environment-constants';

import type {ClientRecord} from 'stores/session-store';

const deployment = __DEPLOYMENT__;
const environment = deployment.includes('production') ? 'production' : deployment;

const isDatadogEnabled = __IS_DATADOG_ENABLED__;
const logger = new Logger('main');

/**
 * Confluence: https://blinemedical.atlassian.net/wiki/spaces/SD/pages/3164078092
 */
export const useDatadogRum = (client: ClientRecord) => {
    const {disableDatadogRumBrowserSdk} = useFlags();
    const regionInUnitedStates = useMemo(() => isRegionInUnitedStates(__AWS_REGION__), [__AWS_REGION__]);

    useEffect(() => {
        if (disableDatadogRumBrowserSdk === undefined || disableDatadogRumBrowserSdk || !isDatadogEnabled) {
            return;
        }

        const initConfiguration = datadogRum.getInitConfiguration();

        // Avoid reinitializing
        if (initConfiguration) {
            return;
        }

        try {
            let ddApplicationId: string;
            let ddClientToken: string;
            if (environment === 'production') {
                ddApplicationId = EnvironmentConstants.DATADOG_PRODUCTION_SIMCAPTURE_CLOUD_APPLICATION_ID;
                ddClientToken = EnvironmentConstants.DATADOG_PRODUCTION_SIMCAPTURE_CLOUD_CLIENT_TOKEN;
            } else {
                // All non-production deployments use the development application.
                ddApplicationId = EnvironmentConstants.DATADOG_DEVELOPMENT_SIMCAPTURE_CLOUD_APPLICATION_ID;
                ddClientToken = EnvironmentConstants.DATADOG_DEVELOMENT_SIMCAPTURE_CLOUD_CLIENT_TOKEN;
            }

            datadogRum.init({
                clientToken: ddClientToken,
                env: environment,
                site: 'datadoghq.com',
                service: EnvironmentConstants.SERVICE_CONSUMERS.SIMCAPTURE_CLOUD,
                version: formatVersionForDatadog(__VERSION__),
                sessionSampleRate: 100,
                applicationId: ddApplicationId,
                silentMultipleInit: true,
                allowedTracingUrls: [
                    // Add datadog tracing headers to requests to simcapture API
                    // so that we can tie rum sessions to apm traces
                    {
                        match: /https:\/\/.*\.simcapture-dev\.com.*/,
                        propagatorTypes: ['datadog', 'tracecontext'],
                    },
                    {
                        match: /https:\/\/api.*\.simcapture-staging\.com.*/,
                        propagatorTypes: ['datadog', 'tracecontext'],
                    },
                    {
                        match: /https:\/\/api.*\.simcapture\.com.*/,
                        propagatorTypes: ['datadog', 'tracecontext'],
                    },
                ],
                beforeSend: (event) => {
                    event.context = {
                        ...event.context,
                        deployment,
                        ui_service: __UI_SERVICE__,
                    };

                    return true;
                },
                defaultPrivacyLevel: regionInUnitedStates ? 'allow' : 'mask-user-input',
                sessionReplaySampleRate: 100,
            });
        } catch (err) {
            logger.error(`Failed to initialize Datadog SDK for environment: ${environment}`, err);
        }
    }, [disableDatadogRumBrowserSdk, isDatadogEnabled, environment, regionInUnitedStates]);

    useEffect(() => {
        if (!regionInUnitedStates) {
            return;
        }

        if (client.get('clientId')) {
            datadogRum.setAccount({
                id: client.get('clientId'),
                institutionName: client.get('institutionName'),
            });
        } else {
            datadogRum.clearAccount();
        }
    }, [regionInUnitedStates, client.get('clientId')]);
};
